import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
  login as apiLogin, 
  logout as apiLogout, 
  isLoggedIn, 
  getCurrentUser 
} from '../services/auth';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const authStatus = await isLoggedIn();
      
      if (authStatus.isLoggedIn) {
        setUser(authStatus.user);
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    } catch (error) {
      // console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      // console.log('🔐 [AuthContext] Calling apiLogin...');
      const result = await apiLogin(email, password);
      // console.log('✅ [AuthContext] apiLogin result:', result);
      
      // Store user state
      setUser(result.user);
      
      return { 
        success: true, 
        user: result.user 
      };
      
    } catch (error) {
      // console.error('🔥 [AuthContext] Login error:', error);
    
      // Check if it's a verification error
      if (error.needsVerification) {
        // console.log('⚠️ [AuthContext] Throwing verification error');
        throw error;
      }
      
      // Return failure with message
      return { 
        success: false, 
        message: error.message || 'Login failed' 
      };
    }
  };

  const logout = async () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      // Optional: Redirect to login
      // window.location.href = '/login';
      return { success: true };
    } catch (error) {
      // console.error('Logout error:', error);
      return { success: false, message: error.message };
    }
  };

  // NEW: Function to update user profile
  const updateUserProfile = (updatedData) => {
    if (!user) return;
    
    setUser(prevUser => ({
      ...prevUser,
      ...updatedData
    }));
    
    // Also update in localStorage if you store user data there
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        const updatedUser = { ...parsedUser, ...updatedData };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } catch (error) {
        // console.error('Error updating user in localStorage:', error);
      }
    }
  };

  // NEW: Function to refresh user data from API
  const refreshUserData = async () => {
    try {
      const authStatus = await isLoggedIn();
      if (authStatus.isLoggedIn) {
        setUser(authStatus.user);
        return { success: true, user: authStatus.user };
      }
      return { success: false };
    } catch (error) {
      // console.error('Error refreshing user data:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    updateUserProfile, // Add this
    refreshUserData,   // Add this
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};